import { useFormik } from "formik";
import * as yup from 'yup';
import { Button, Input } from "@netfront/ui-library";
import { IShareSummaryFormFields, ShareSummaryFormProps } from "./ShareSummaryForm.interfaces";
import { useEffect, useState } from "react";
import { useToast } from "@netfront/ekardo-content-library";
import { IUser, useGetLoggedUser } from "@netfront/gelada-identity-library";
import { useSendMailsMutation } from "@/services/howler/generated.graphql";
import { howlerClient } from "@/services";
import { CONSTANTS } from "@/constants";
import { extractS3Info } from "@/utils";

export const ShareSummaryForm = ({ onCompleted }: ShareSummaryFormProps) => {
  const { handleToastError, handleToastSuccess, handleToastCustomError } = useToast();

  const [user, setUser] = useState<IUser>();
  const [isSending, setIsSending] = useState<boolean>(false);

  const [sendMails] = useSendMailsMutation({
    client: howlerClient,
    onCompleted: () => {
      setIsSending(false);

      handleToastSuccess({
        message: 'Your summary has been sent'
      });

      if(onCompleted) {
        onCompleted(true);
      }
    },
    onError: (error) => {
      setIsSending(false);

      handleToastError({
        error,
        shouldUseFriendlyErrorMessage: true,
      });
    },
  });

  const { handleGetLoggedUser } = useGetLoggedUser({
    onCompleted: ({ user: returnedUser }) => {
      setUser(returnedUser);
    },
    onError: error => {
      handleToastError({
        error,
        shouldUseFriendlyErrorMessage: true,
      });
    }
  });

  const formik = useFormik<IShareSummaryFormFields>({
    initialValues: {
      email: ''
    },
    validationSchema: yup.object().shape({
      email: yup.string().email('Please enter a valid email address').required('Please enter in a valid email address'),
    }),
    onSubmit: async ({ email }: IShareSummaryFormFields) => {
      setIsSending(true);

      if (!window.Kanzi_Manual_Commands?.GeneratePdf) {
        setIsSending(false);
        handleToastCustomError({ message: "PDF generation is unavailable. Please try again." });
        return;
      }

      window.Kanzi_Manual_Commands.GeneratePdf((c) => {
        try {
          const { bucket, key } = extractS3Info(c?.preSignedUrl);

          sendMails({
            variables: {
              attachments: [{ bucket: bucket, key: key }],
              placeholders: [],
              subject: 'Summary',
              templateId: CONSTANTS.EMAIL_TEMEPLATES.SHARE_SUMMARY.ID,
              projectId: String(process.env.REACT_APP_PROJECT_ID), 
              receiver: email,
            }
          });
        } catch {
          setIsSending(false);
          handleToastCustomError({ message: "Could not prepare summary attachment." });
        }
      })
    }
  });

  useEffect(() => {
    handleGetLoggedUser();
  }, []);

  useEffect(() => {
    if(!user) return;
    setFieldValue('email', user.credential.email);
  }, [user]);

  const { errors, touched, handleBlur, handleChange, handleSubmit, setFieldValue, values } = formik;

  return (
    <form onSubmit={handleSubmit}>
      <Input
        errorMessage={touched.email && errors.email ? errors.email : ''}
        id='email'
        labelText='Email'
        placeholder='Enter email'
        name='email'
        onBlur={handleBlur}
        onChange={handleChange}
        value={values.email}
        type='email'
        isRequired
      />

      <Button
        text='Send summary'
        type='submit'
        isLoading={isSending}
      />
    </form>
  )
};